import React, { useState, useRef, useEffect } from "react";
import PropTypes from "prop-types";
import { Link } from "react-router-dom";
import Select from "react-select";
import {
  UserPlus,
  Loader2,
  Search,
  Filter,
  X,
  Trash2,
  Pause,
  CreditCard,
  Mail,
  Phone,
  MapPin,
  List,
  ArrowLeft,
  Tag,
  ShieldCheck,
  RotateCcw,
  ScanBarcode,
  Package,
  AlertCircle,
} from "lucide-react";
import CartItem from "./CartItem";
import {
  normalizeSerial,
  isSerialInCart,
  findDuplicateSerialsInCart,
  filterAvailableProductSerials,
} from "@/lib/posSerialUtils";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Separator } from "@/components/ui/separator";

const CartSidebar = ({
  customer,
  customerSearchExpanded,
  setCustomerSearchExpanded,
  customerLoading,
  customers,
  customerNameLabel,
  debouncedSearchCustomers,
  cart,
  setCart,
  fetchCustomer,
  updateCart,
  hasPermission,
  setModalOpen,
  searchInputRef,
  searchDropdownProducts,
  showToast,
  setFilterModalOpen,
  selectedBrand,
  priceFilter,
  currentProducts,
  clearAllItems,
  currencySymbol,
  localQuantities,
  handleQuantityChange,
  updatingItemIds,
  handleEditCartItem,
  removeCartItem,
  calculateSubTotal,
  calculateTotal,
  qtyUpdating,
  fetchRecentOrders,
  onCancelOrder = () => { },
  onHoldOrder = () => { },
  completing,
  completeOrder,
  currentSaleData = null,
  paidAmount = 0,
  balanceDue = 0,
  onExitSale = () => { },
  onClaimWarranty,
  addCartItem,
}) => {
  const [sidebarSearchText, setSidebarSearchText] = useState("");
  const [dropdownProducts, setDropdownProducts] = useState([]);
  const [dropdownLoading, setDropdownLoading] = useState(false);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const searchContainerRef = useRef(null);
  const sidebarSearchTimeoutRef = useRef(null);
  const lastInputTime = useRef(0);

  // Close dropdown on click outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        searchContainerRef.current &&
        !searchContainerRef.current.contains(event.target)
      ) {
        setIsDropdownOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  // Auto-focus left sidebar search input on mount so barcode scanners work immediately
  useEffect(() => {
    const focusSearch = () => {
      if (searchInputRef?.current) {
        searchInputRef.current.focus();
      }
    };

    focusSearch();
    const timer = setTimeout(focusSearch, 200);
    return () => clearTimeout(timer);
  }, [searchInputRef]);

  const cartDuplicateSerials = React.useMemo(() => {
    return findDuplicateSerialsInCart(currentProducts);
  }, [currentProducts]);

  const handleSidebarSearchChange = (e) => {
    const value = e.target.value;
    setSidebarSearchText(value);
    setHighlightedIndex(-1);

    if (sidebarSearchTimeoutRef.current) {
      clearTimeout(sidebarSearchTimeoutRef.current);
    }

    const trimmed = value.trim();
    if (!trimmed) {
      setDropdownProducts([]);
      setDropdownLoading(false);
      setIsDropdownOpen(false);
      return;
    }

    setIsDropdownOpen(true);
    setDropdownLoading(true);

    sidebarSearchTimeoutRef.current = setTimeout(async () => {
      if (searchDropdownProducts) {
        const results = await searchDropdownProducts(trimmed);
        setDropdownProducts(results || []);
      }
      setDropdownLoading(false);
    }, 250);
  };

  const handleClearSidebarSearch = () => {
    setSidebarSearchText("");
    setDropdownProducts([]);
    setDropdownLoading(false);
    setIsDropdownOpen(false);
    setHighlightedIndex(-1);
    if (sidebarSearchTimeoutRef.current) {
      clearTimeout(sidebarSearchTimeoutRef.current);
    }
    if (searchInputRef?.current) {
      searchInputRef.current.focus();
    }
  };

  const handleSelectProduct = async (product, defaultSerial = null) => {
    handleClearSidebarSearch();
    if (addCartItem) {
      await addCartItem(product, 1, defaultSerial);
    }
    if (searchInputRef?.current) {
      searchInputRef.current.focus();
    }
  };

  const handleCustomKeyDown = async (e) => {
    const now = Date.now();
    lastInputTime.current = now;

    if (e.key === "ArrowDown") {
      if (isDropdownOpen && dropdownProducts.length > 0) {
        e.preventDefault();
        setHighlightedIndex((prev) => (prev + 1) % dropdownProducts.length);
        return;
      }
    }

    if (e.key === "ArrowUp") {
      if (isDropdownOpen && dropdownProducts.length > 0) {
        e.preventDefault();
        setHighlightedIndex(
          (prev) => (prev - 1 + dropdownProducts.length) % dropdownProducts.length
        );
        return;
      }
    }

    if (e.key === "Escape") {
      e.preventDefault();
      setIsDropdownOpen(false);
      return;
    }

    if (e.key === "Enter") {
      e.preventDefault();
      const trimmed = sidebarSearchText.trim();
      if (!trimmed) return;

      if (sidebarSearchTimeoutRef.current) {
        clearTimeout(sidebarSearchTimeoutRef.current);
      }

      // 0. Immediate guard: if the scanned code is already in the cart as a serial number
      if (isSerialInCart(currentProducts, trimmed)) {
        if (showToast) {
          showToast(
            `Serial number "${trimmed}" is already in the cart.`,
            "error",
          );
        }
        handleClearSidebarSearch();
        return;
      }

      const processSelectedProduct = async (selectedProd) => {
        const qty = selectedProd.manage_inventory
          ? parseFloat(selectedProd.sellable_qty || 0)
          : 999;
        if (qty <= 0) {
          if (showToast) {
            showToast("This product is out of stock.", "error");
          }
          handleClearSidebarSearch();
          return;
        }

        const isSer =
          selectedProd.manage_inventory &&
          (selectedProd.tracking_type === "serial" ||
            selectedProd.is_serialized);

        if (isSer) {
          const raw = Array.isArray(selectedProd.serial_numbers)
            ? selectedProd.serial_numbers
            : [];
          const avail = filterAvailableProductSerials(raw, currentProducts);
          const term = trimmed.toLowerCase();

          // If user specifically entered/scanned a serial belonging to this product that is already in cart
          const isRawSerial = raw.some(
            (s) => normalizeSerial(s).toLowerCase() === term,
          );
          if (isRawSerial) {
            const isAvail = avail.some(
              (s) => normalizeSerial(s).toLowerCase() === term,
            );
            if (!isAvail) {
              if (showToast) {
                showToast(
                  `Serial number "${trimmed}" is already in the cart.`,
                  "error",
                );
              }
              handleClearSidebarSearch();
              return;
            }
          }

          if (raw.length > 0 && avail.length === 0) {
            if (showToast) {
              showToast(
                "All available serial numbers for this product are already in the cart.",
                "error",
              );
            }
            handleClearSidebarSearch();
            return;
          }

          const matches = term
            ? avail.filter((s) =>
              normalizeSerial(s).toLowerCase().includes(term),
            )
            : [];
          const autoSn =
            matches.length > 0
              ? normalizeSerial(matches[0])
              : avail.length === 1
                ? normalizeSerial(avail[0])
                : null;

          await handleSelectProduct(selectedProd, autoSn);
        } else {
          await handleSelectProduct(selectedProd, null);
        }
      };

      // 1. If user highlighted a specific product via arrows
      if (
        isDropdownOpen &&
        highlightedIndex >= 0 &&
        highlightedIndex < dropdownProducts.length
      ) {
        await processSelectedProduct(dropdownProducts[highlightedIndex]);
        return;
      }

      // 2. If dropdown already has exactly 1 product
      if (isDropdownOpen && dropdownProducts.length === 1) {
        await processSelectedProduct(dropdownProducts[0]);
        return;
      }

      // 3. Fast barcode scan or enter before dropdown finishes loading
      if (searchDropdownProducts) {
        setDropdownLoading(true);
        const results = await searchDropdownProducts(trimmed);
        setDropdownLoading(false);
        if (results && results.length === 1) {
          await processSelectedProduct(results[0]);
        } else if (results && results.length === 0) {
          if (showToast) {
            showToast(
              `Barcode / Item not recognised: "${trimmed}".`,
              "error",
            );
          }
          handleClearSidebarSearch();
        } else {
          setDropdownProducts(results || []);
          setIsDropdownOpen(true);
        }
      }
    }
  };

  const handleDiscountTypeChange = (type) => {
    let newDiscountValue = cart.discount_value || 0;
    if (type === "none") {
      newDiscountValue = 0;
    } else if (type === "percentage" && newDiscountValue > 100) {
      newDiscountValue = 100;
    }
    const subtotal = calculateSubTotal ? calculateSubTotal() : 0;
    let newDiscountAmount = 0;
    if (type === "percentage" && newDiscountValue > 0) {
      newDiscountAmount = Math.min((subtotal * newDiscountValue) / 100, subtotal);
    } else if (type === "fixed" && newDiscountValue > 0) {
      newDiscountAmount = Math.min(newDiscountValue, subtotal);
    }
    setCart({
      ...cart,
      discount_type: type,
      discount_value: newDiscountValue,
      discount_amount: newDiscountAmount,
    });
  };

  const handleQuickPercent = (pct) => {
    const subtotal = calculateSubTotal ? calculateSubTotal() : 0;
    const newDiscountAmount = Math.min((subtotal * pct) / 100, subtotal);
    setCart({
      ...cart,
      discount_type: "percentage",
      discount_value: pct,
      discount_amount: newDiscountAmount,
    });
  };

  return (
    <div className="flex flex-col h-full bg-card border-r border-border shadow-sm min-w-0">
      {/* Navigation Header */}
      <div className="flex items-center gap-2 p-2 border-b border-border bg-background shrink-0">
        <Link to="/">
          <Button variant="ghost" size="sm" className="h-8 px-2">
            <ArrowLeft size={16} className="mr-1.5" />
            <span className="text-xs font-medium">Dashboard</span>
          </Button>
        </Link>
        <div className="flex-1 text-center">
          <h2 className="font-semibold text-sm">Point of Sale</h2>
        </div>
        <div className="w-[85px]"></div> {/* Adjusted Spacer */}
      </div>

      {/* Search & Customer Section */}
      <div className="p-2 space-y-2 border-b border-border bg-background shrink-0">

        {/* Customer Details Card */}
        <div className="rounded-lg border border-border bg-muted/30 p-2">
          <div className="flex items-center justify-between">
            <div className="flex-1 min-w-0">
              {customer ? (
                <div className="space-y-1">
                  <h6 className="font-semibold text-sm truncate">
                    {customer.customer_type !== "individual" && customer.company
                      ? customer.company
                      : customer.full_name}
                  </h6>
                  <div className="flex flex-wrap gap-x-3 gap-y-1 text-xs text-muted-foreground">
                    {customer.email && customer.email !== "-" && (
                      <div className="flex items-center gap-1">
                        <Mail size={12} />
                        <span className="truncate max-w-[150px]">
                          {customer.email}
                        </span>
                      </div>
                    )}
                    {customer.phone && (
                      <div className="flex items-center gap-1">
                        <Phone size={12} />
                        <span>{customer.phone}</span>
                      </div>
                    )}
                  </div>
                  {customer.street_address && (
                    <div className="flex items-center gap-1 text-xs text-muted-foreground truncate">
                      <MapPin size={12} className="shrink-0" />
                      <span className="truncate">
                        {customer.house_number && `${customer.house_number}, `}
                        {customer.street_address}
                        {customer.city && `, ${customer.city}`}
                        {customer.postcode && ` ${customer.postcode}`}
                      </span>
                    </div>
                  )}
                </div>
              ) : (
                <div className="text-sm text-muted-foreground italic">
                  No customer selected
                </div>
              )}
            </div>

            {!currentSaleData && (
              <div className="flex items-center gap-2 ml-2">
                {!customerSearchExpanded ? (
                  <Button
                    variant="outline"
                    size="icon"
                    className="h-9 w-9 shrink-0"
                    onClick={() => setCustomerSearchExpanded(true)}
                    title="Search Customer"
                  >
                    <Search size={16} />
                  </Button>
                ) : (
                  <div className="w-[200px]">
                    <Select
                      classNamePrefix="react-select"
                      menuPortalTarget={document.body}
                      isLoading={customerLoading}
                      filterOption={() => true}
                      styles={{
                        menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                        control: (base) => ({
                          ...base,
                          minHeight: "36px",
                          fontSize: "0.75rem",
                        }),
                      }}
                      options={customers.map((customer) => ({
                        value: customer.id,
                        label: customerNameLabel(customer),
                      }))}
                      placeholder="Search..."
                      onInputChange={(inputValue) => {
                        if (inputValue.length > 2) {
                          debouncedSearchCustomers(inputValue);
                        }
                        return inputValue;
                      }}
                      onChange={(e) => {
                        if (e && e.value && e.value !== cart.customer_id) {
                          const updatedCart = { ...cart, customer_id: e.value };
                          setCart(updatedCart);
                          fetchCustomer(e.value);
                          if (cart.id) {
                            updateCart(updatedCart);
                          }
                          setCustomerSearchExpanded(false);
                        }
                      }}
                      onBlur={() =>
                        setTimeout(() => setCustomerSearchExpanded(false), 200)
                      }
                      autoFocus
                      menuIsOpen={true}
                    />
                  </div>
                )}
                {hasPermission("customer create") && (
                  <Button
                    size="icon"
                    className="h-9 w-9 shrink-0"
                    onClick={() => setModalOpen(true)}
                    title="Add New Customer"
                  >
                    <UserPlus size={16} />
                  </Button>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Product Search */}
        <div className="flex gap-2">
          <div className="relative flex-1" ref={searchContainerRef}>
            <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
            <Input
              ref={searchInputRef}
              type="text"
              autoFocus
              className={`h-9 pl-8 text-sm ${sidebarSearchText ? "pr-8" : ""}`}
              placeholder="Scan/Search Products..."
              value={sidebarSearchText}
              onChange={handleSidebarSearchChange}
              onFocus={() => {
                if (sidebarSearchText && sidebarSearchText.trim().length > 0) {
                  setIsDropdownOpen(true);
                }
              }}
              onKeyDown={handleCustomKeyDown}
            />
            {sidebarSearchText && (
              <button
                type="button"
                onClick={handleClearSidebarSearch}
                className="absolute right-2.5 top-2.5 text-muted-foreground hover:text-foreground cursor-pointer"
                title="Clear Search"
              >
                <X size={15} />
              </button>
            )}

            {/* Dropdown Results */}
            {isDropdownOpen && sidebarSearchText && sidebarSearchText.trim().length > 0 && (
              <div
                className="absolute top-full left-0 right-0 mt-1.5 z-50 bg-popover text-popover-foreground rounded-lg border border-border shadow-2xl overflow-hidden flex flex-col animate-in fade-in-0 zoom-in-95 duration-100"
                style={{ maxHeight: "420px" }}
              >
                {/* Header */}
                <div className="flex items-center justify-between px-3 py-2 bg-muted/60 border-b border-border/60 text-xs font-medium text-muted-foreground shrink-0">
                  <span className="flex items-center gap-1.5">
                    {dropdownLoading ? (
                      <>
                        <Loader2 className="h-3.5 w-3.5 animate-spin text-primary" />
                        <span>Searching...</span>
                      </>
                    ) : (
                      <span>
                        {dropdownProducts.length} product{dropdownProducts.length === 1 ? "" : "s"} found
                      </span>
                    )}
                  </span>
                  <span className="text-[11px] opacity-75">Click item or serial to add to cart</span>
                </div>

                {/* List */}
                <div className="overflow-y-auto max-h-[360px] divide-y divide-border/40">
                  {dropdownLoading && dropdownProducts.length === 0 ? (
                    <div className="py-8 px-4 flex flex-col items-center justify-center gap-2 text-muted-foreground">
                      <Loader2 className="h-5 w-5 animate-spin text-primary" />
                      <span className="text-xs">Searching products & serials...</span>
                    </div>
                  ) : dropdownProducts.length === 0 ? (
                    <div className="py-8 px-4 text-center text-muted-foreground">
                      <Package className="h-8 w-8 mx-auto mb-2 opacity-30" />
                      <p className="text-xs font-semibold text-foreground">No products or serials found</p>
                      <p className="text-[11px] mt-0.5">No matches for &ldquo;{sidebarSearchText}&rdquo;</p>
                    </div>
                  ) : (
                    dropdownProducts.map((product, idx) => {
                      const isHighlighted = idx === highlightedIndex;
                      const sellableQty = product.manage_inventory
                        ? parseFloat(product.sellable_qty || 0)
                        : null;
                      const isOutOfStock = product.manage_inventory && sellableQty <= 0;
                      const imageUrl = product.media?.[0]?.url || product.image;
                      const price = parseFloat(
                        product.sell_price || product.retail_price || product.price || 0
                      );
                      const isSerialized =
                        product.manage_inventory &&
                        (product.tracking_type === "serial" || product.is_serialized);

                      const rawSerials = Array.isArray(product.serial_numbers)
                        ? product.serial_numbers
                        : [];
                      const availableSerials = isSerialized
                        ? filterAvailableProductSerials(rawSerials, currentProducts)
                        : [];
                      const searchTermLower = (sidebarSearchText || "").toLowerCase().trim();
                      const matchingSerials =
                        isSerialized && searchTermLower
                          ? availableSerials.filter((s) => {
                            const sn = normalizeSerial(s);
                            return sn.toLowerCase().includes(searchTermLower);
                          })
                          : [];

                      const autoSerial =
                        matchingSerials.length > 0
                          ? normalizeSerial(matchingSerials[0])
                          : availableSerials.length === 1
                            ? normalizeSerial(availableSerials[0])
                            : null;

                      const allSerialsInCart =
                        isSerialized &&
                        !isOutOfStock &&
                        rawSerials.length > 0 &&
                        availableSerials.length === 0;

                      return (
                        <div
                          key={product.id}
                          onClick={() => {
                            if (isOutOfStock) {
                              if (showToast) {
                                showToast(
                                  "This product is out of stock.",
                                  "error",
                                );
                              }
                              return;
                            }
                            if (allSerialsInCart) {
                              if (showToast) {
                                showToast(
                                  "All serial numbers for this product are already in the cart.",
                                  "error",
                                );
                              }
                              return;
                            }
                            handleSelectProduct(product, autoSerial);
                          }}
                          className={`p-2.5 transition-colors cursor-pointer ${isHighlighted ? "bg-accent" : "hover:bg-accent/60"
                            } ${isOutOfStock || allSerialsInCart
                              ? "opacity-60 cursor-not-allowed hover:bg-transparent"
                              : ""
                            }`}
                        >
                          <div className="flex items-start gap-2.5">
                            {/* Product Thumbnail */}
                            <div className="w-10 h-10 rounded-md overflow-hidden bg-muted border border-border/60 shrink-0 flex items-center justify-center">
                              {imageUrl ? (
                                <img
                                  src={imageUrl}
                                  alt={product.name}
                                  className="w-full h-full object-cover"
                                />
                              ) : (
                                <Package className="h-5 w-5 text-muted-foreground/60" />
                              )}
                            </div>

                            {/* Product Info */}
                            <div className="flex-1 min-w-0">
                              <div className="flex items-start justify-between gap-2">
                                <div className="min-w-0">
                                  <h6
                                    className="font-semibold text-xs text-foreground leading-snug truncate"
                                    title={product.name}
                                  >
                                    {product.name}
                                  </h6>
                                  <div className="flex items-center gap-1.5 flex-wrap text-[10px] text-muted-foreground mt-0.5">
                                    {product.sku && (
                                      <span className="font-mono bg-muted px-1 py-0.2 rounded border border-border/40">
                                        {product.sku}
                                      </span>
                                    )}
                                    {product.brand?.name && <span>{product.brand.name}</span>}
                                    {product.model?.name && <span>• {product.model.name}</span>}
                                  </div>
                                </div>

                                <div className="text-right shrink-0">
                                  <div className="font-bold text-xs text-primary">
                                    {currencySymbol}
                                    {price.toFixed(2)}
                                  </div>
                                  <div className="mt-0.5">
                                    {product.manage_inventory ? (
                                      isOutOfStock ? (
                                        <Badge
                                          variant="destructive"
                                          className="text-[9px] px-1 py-0 h-3.5"
                                        >
                                          Out of stock
                                        </Badge>
                                      ) : (
                                        <Badge
                                          variant="outline"
                                          className="text-[9px] px-1 py-0 h-3.5 font-normal bg-emerald-50 text-emerald-700 dark:bg-emerald-950 dark:text-emerald-300 border-emerald-200 dark:border-emerald-800"
                                        >
                                          {sellableQty} in stock
                                        </Badge>
                                      )
                                    ) : (
                                      <Badge
                                        variant="outline"
                                        className="text-[9px] px-1 py-0 h-3.5 font-normal bg-blue-50 text-blue-700 dark:bg-blue-950 dark:text-blue-300 border-blue-200"
                                      >
                                        In stock
                                      </Badge>
                                    )}
                                  </div>
                                </div>
                              </div>

                              {/* Serials Sub-section */}
                              {isSerialized && !isOutOfStock && rawSerials.length > 0 && (
                                <div className="mt-2 pt-1.5 border-t border-border/40">
                                  {matchingSerials.length > 0 ? (
                                    <div>
                                      <div className="text-[10px] font-semibold text-emerald-600 dark:text-emerald-400 mb-1 flex items-center gap-1">
                                        <ScanBarcode className="h-3 w-3" />
                                        <span>Matching Serial{matchingSerials.length > 1 ? `s (${matchingSerials.length})` : ""}:</span>
                                      </div>
                                      <div className="flex flex-wrap gap-1">
                                        {matchingSerials.map((serial) => {
                                          const sn = normalizeSerial(serial);
                                          return (
                                            <span
                                              key={serial.id || sn}
                                              onClick={(e) => {
                                                e.stopPropagation();
                                                handleSelectProduct(product, sn);
                                              }}
                                              title={`Add serial ${sn}`}
                                              className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[11px] font-mono font-medium bg-emerald-100 text-emerald-800 dark:bg-emerald-950 dark:text-emerald-300 border border-emerald-300 dark:border-emerald-800 shadow-2xs hover:bg-emerald-200 cursor-pointer"
                                            >
                                              <ScanBarcode className="h-2.5 w-2.5" />
                                              <span>{sn}</span>
                                            </span>
                                          );
                                        })}
                                      </div>
                                    </div>
                                  ) : availableSerials.length > 0 ? (
                                    <div>
                                      <div className="text-[10px] text-muted-foreground mb-1 flex items-center gap-1">
                                        <ScanBarcode className="h-3 w-3" />
                                        <span>Available Serials ({availableSerials.length}):</span>
                                      </div>
                                      <div className="flex flex-wrap gap-1">
                                        {availableSerials.slice(0, 4).map((serial) => {
                                          const sn = normalizeSerial(serial);
                                          return (
                                            <span
                                              key={serial.id || sn}
                                              onClick={(e) => {
                                                e.stopPropagation();
                                                handleSelectProduct(product, sn);
                                              }}
                                              title={`Add serial ${sn}`}
                                              className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-mono bg-muted/80 text-muted-foreground border border-border/80 hover:bg-primary/10 hover:text-primary cursor-pointer transition-colors"
                                            >
                                              <ScanBarcode className="h-2.5 w-2.5 text-muted-foreground/70" />
                                              <span>{sn}</span>
                                            </span>
                                          );
                                        })}
                                        {availableSerials.length > 4 && (
                                          <span className="text-[10px] text-muted-foreground self-center px-1">
                                            +{availableSerials.length - 4} more
                                          </span>
                                        )}
                                      </div>
                                    </div>
                                  ) : allSerialsInCart ? (
                                    <div className="text-[10px] text-amber-600 dark:text-amber-400 italic">
                                      All serial numbers are in cart
                                    </div>
                                  ) : null}
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      );
                    })
                  )}
                </div>
              </div>
            )}
          </div>
          <Button
            variant="outline"
            className="h-9 px-2.5"
            onClick={() => setFilterModalOpen(true)}
            title="Filter Products"
          >
            <Filter size={16} />
            {(selectedBrand || priceFilter.min || priceFilter.max) && (
              <Badge
                variant="destructive"
                className="ml-2 h-5 w-5 p-0 flex items-center justify-center rounded-full text-[10px]"
              >
                {[
                  selectedBrand ? 1 : 0,
                  priceFilter.min || priceFilter.max ? 1 : 0,
                ].reduce((a, b) => a + b, 0)}
              </Badge>
            )}
          </Button>
        </div>
      </div>

      {/* Cart Items List */}
      <div className="flex-1 flex flex-col min-h-0 bg-background">
        <div className="flex items-center justify-between px-3 py-1.5 bg-muted/40 border-b border-border">
          <h6 className="text-xs font-semibold flex items-center gap-2">
            {currentSaleData ? "Sale Items" : "Products Added"}
            <Badge
              variant="secondary"
              className="px-1.5 h-4 min-w-[1.25rem] text-[10px]"
            >
              {currentProducts.length}
            </Badge>
            {currentSaleData && (
              <Badge
                variant="outline"
                className="text-[9px] h-4 px-1.5 bg-background text-muted-foreground border-border font-normal"
              >
                Read-Only
              </Badge>
            )}
          </h6>
          {currentSaleData ? (
            <Button
              variant="ghost"
              size="sm"
              className="text-muted-foreground hover:text-foreground h-8 px-2 text-xs font-medium cursor-pointer"
              onClick={onExitSale}
              title="Exit Sale View"
            >
              <X size={14} className="mr-1" /> Exit Sale
            </Button>
          ) : hasPermission?.("cart delete") ? (
            <Button
              variant="ghost"
              size="sm"
              className="text-destructive hover:text-destructive hover:bg-destructive/10 h-8 px-2 text-xs font-medium"
              onClick={clearAllItems}
              disabled={!currentProducts || currentProducts.length === 0}
            >
              <X size={14} className="mr-1" /> Clear all
            </Button>
          ) : null}
        </div>

        {cartDuplicateSerials.length > 0 && (
          <div className="mx-2 my-2 p-2.5 rounded-md bg-destructive/15 border border-destructive/30 text-destructive text-xs font-medium flex items-start gap-2">
            <AlertCircle className="h-4 w-4 shrink-0 mt-0.5" />
            <div className="flex-1 min-w-0">
              <p className="font-semibold">Duplicate serial numbers detected:</p>
              <p className="font-mono text-[11px] truncate">
                {cartDuplicateSerials.join(", ")}
              </p>
              <p className="text-[10px] text-destructive/80 mt-0.5">
                Duplicate serials cannot be submitted. Please remove the duplicate items before checking out.
              </p>
            </div>
          </div>
        )}

        <ScrollArea className="flex-1">
          <div className="p-0">
            {currentProducts && currentProducts.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-12 text-muted-foreground px-4 text-center">
                <div className="h-12 w-12 rounded-full bg-muted flex items-center justify-center mb-3">
                  <List size={24} />
                </div>
                <p>
                  {currentSaleData
                    ? "This sale has no recorded items."
                    : "No products added directly to cart!"}
                </p>
                <p className="text-sm">
                  {currentSaleData
                    ? "Check sale details or payment history."
                    : "Scan barcode or search products to begin."}
                </p>
              </div>
            ) : (
              currentProducts.map((product) => (
                <CartItem
                  key={product.id}
                  product={product}
                  currencySymbol={currencySymbol}
                  localQuantities={localQuantities}
                  handleQuantityChange={handleQuantityChange}
                  updatingItemIds={updatingItemIds}
                  handleEditCartItem={handleEditCartItem}
                  removeCartItem={removeCartItem}
                  cart={cart}
                  setCart={setCart}
                  hasPermission={hasPermission}
                  isSale={Boolean(currentSaleData)}
                  onClaimWarranty={onClaimWarranty}
                  duplicateSerials={cartDuplicateSerials}
                />
              ))
            )}
          </div>
        </ScrollArea>
      </div>

      {/* Footer Section */}
      <div className="bg-background border-t border-border p-2.5 space-y-2.5 shrink-0">
        {/* Discount Section */}
        {currentSaleData ? (
          <div className="flex items-center justify-between p-2 rounded-lg bg-muted/30 border border-border/60 text-xs">
            <span className="text-[11px] font-semibold text-muted-foreground flex items-center gap-1.5 uppercase tracking-wider">
              <Tag size={12} className="text-primary" />
              Discount
            </span>
            <span className="font-semibold text-foreground text-xs">
              {cart.discount_type !== "none" &&
                (parseFloat(cart.discount_value) > 0 ||
                  parseFloat(cart.discount_amount) > 0)
                ? `${cart.discount_type === "percentage"
                  ? `${cart.discount_value}%`
                  : `${currencySymbol}${parseFloat(
                    cart.discount_value || cart.discount_amount,
                  ).toFixed(2)}`
                } (-${currencySymbol}${parseFloat(
                  cart.discount_amount || 0,
                ).toFixed(2)})`
                : "None"}
            </span>
          </div>
        ) : (
          <div className="space-y-1.5 p-2 rounded-lg bg-muted/30 border border-border/60">
            <div className="flex items-center justify-between gap-2">
              <span className="text-[11px] font-semibold text-muted-foreground flex items-center gap-1.5 uppercase tracking-wider">
                <Tag size={12} className="text-primary" />
                Discount
              </span>

              {/* Segmented Type Switcher */}
              <div className="inline-flex items-center rounded-md bg-muted/80 p-0.5 border border-border/60 shadow-2xs text-[11px]">
                <button
                  type="button"
                  className={`px-2 py-0.5 rounded text-[11px] font-medium transition-all ${(cart.discount_type || "none") === "none"
                    ? "bg-primary text-primary-foreground shadow-2xs font-semibold"
                    : "text-muted-foreground hover:text-foreground"
                    }`}
                  onClick={() => handleDiscountTypeChange("none")}
                >
                  None
                </button>
                <button
                  type="button"
                  className={`px-2 py-0.5 rounded text-[11px] font-medium transition-all ${cart.discount_type === "percentage"
                    ? "bg-primary text-primary-foreground shadow-2xs font-semibold"
                    : "text-muted-foreground hover:text-foreground"
                    }`}
                  onClick={() => handleDiscountTypeChange("percentage")}
                >
                  Percentage
                </button>
                <button
                  type="button"
                  className={`px-2 py-0.5 rounded text-[11px] font-medium transition-all ${cart.discount_type === "fixed"
                    ? "bg-primary text-primary-foreground shadow-2xs font-semibold"
                    : "text-muted-foreground hover:text-foreground"
                    }`}
                  onClick={() => handleDiscountTypeChange("fixed")}
                >
                  Fixed
                </button>
              </div>
            </div>

            {/* Value Input & Quick Presets (shown when discount is active) */}
            {cart.discount_type && cart.discount_type !== "none" && (
              <div className="flex items-center gap-2 pt-0.5">
                <div className="relative flex-1 min-w-0">
                  <span className="absolute left-2.5 top-1/2 -translate-y-1/2 text-xs font-semibold text-muted-foreground select-none pointer-events-none">
                    {cart.discount_type === "percentage" ? "%" : currencySymbol}
                  </span>
                  <Input
                    type="number"
                    min="0"
                    max={cart.discount_type === "percentage" ? 100 : undefined}
                    step={cart.discount_type === "percentage" ? "1" : "0.01"}
                    className="h-8 pl-8 pr-7 text-xs font-semibold tabular-nums bg-background"
                    placeholder={
                      cart.discount_type === "percentage" ? "0 - 100%" : "0.00"
                    }
                    value={
                      cart.discount_value === 0 ? "" : (cart.discount_value ?? "")
                    }
                    onChange={(e) => {
                      const raw = e.target.value;
                      if (raw === "") {
                        setCart({ ...cart, discount_value: 0, discount_amount: 0 });
                        return;
                      }
                      let val = parseFloat(raw);
                      if (isNaN(val) || val < 0) val = 0;
                      if (cart.discount_type === "percentage" && val > 100) val = 100;
                      const subtotal = calculateSubTotal ? calculateSubTotal() : 0;
                      let newDiscountAmount = 0;
                      if (cart.discount_type === "percentage") {
                        newDiscountAmount = Math.min((subtotal * val) / 100, subtotal);
                      } else if (cart.discount_type === "fixed") {
                        newDiscountAmount = Math.min(val, subtotal);
                      }
                      setCart({
                        ...cart,
                        discount_value: val,
                        discount_amount: newDiscountAmount,
                      });
                    }}
                  />
                  {Boolean(cart.discount_value) && (
                    <button
                      type="button"
                      className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground p-0.5"
                      onClick={() =>
                        setCart({ ...cart, discount_value: 0, discount_amount: 0 })
                      }
                      title="Clear discount value"
                    >
                      <X size={12} />
                    </button>
                  )}
                </div>

                {/* Quick % chips */}
                {cart.discount_type === "percentage" && (
                  <div className="flex items-center gap-1 shrink-0">
                    {[5, 10, 15, 20].map((pct) => (
                      <Button
                        key={pct}
                        type="button"
                        variant={cart.discount_value === pct ? "default" : "outline"}
                        size="sm"
                        className="h-8 px-2 text-[11px] font-semibold rounded-md shadow-2xs"
                        onClick={() => handleQuickPercent(pct)}
                      >
                        {pct}%
                      </Button>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {/* Order Summary Card */}
        <div className="rounded-xl border border-border/80 bg-muted/20 p-2.5 space-y-2">
          {/* Sub Total */}
          <div className="flex justify-between items-center text-xs text-muted-foreground">
            <span>Sub Total</span>
            <span className="font-semibold text-foreground tabular-nums">
              {currencySymbol}
              {calculateSubTotal().toFixed(2)}
            </span>
          </div>

          {/* Discount */}
          {(() => {
            let totalItemDiscounts = 0;
            currentProducts?.forEach((product) => {
              totalItemDiscounts += parseFloat(product.discount_amount) || 0;
            });
            const subtotal = calculateSubTotal ? calculateSubTotal() : 0;
            let cartDiscountAmount = 0;
            if (cart.discount_type === "percentage" && cart.discount_value > 0) {
              cartDiscountAmount = Math.min(
                (subtotal * parseFloat(cart.discount_value)) / 100,
                subtotal,
              );
            } else if (cart.discount_type === "fixed" && cart.discount_value > 0) {
              cartDiscountAmount = Math.min(
                parseFloat(cart.discount_value),
                subtotal,
              );
            } else {
              cartDiscountAmount = parseFloat(cart.discount_amount) || 0;
            }
            const totalDiscount = totalItemDiscounts + cartDiscountAmount;

            if (totalDiscount > 0) {
              return (
                <div className="flex justify-between items-start text-xs">
                  <span className="text-emerald-600 dark:text-emerald-400 font-medium flex items-center gap-1.5 pt-0.5">
                    <Tag size={12} />
                    <span>Discount</span>
                    {cart.discount_type === "percentage" && cart.discount_value > 0 && (
                      <Badge
                        variant="outline"
                        className="text-[9px] px-1 py-0 h-4 border-emerald-500/30 bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 font-semibold"
                      >
                        {cart.discount_value}% OFF
                      </Badge>
                    )}
                  </span>
                  <div className="flex flex-col items-end">
                    <span className="font-semibold text-emerald-600 dark:text-emerald-400 tabular-nums">
                      - {currencySymbol}
                      {totalDiscount.toFixed(2)}
                    </span>
                    {totalItemDiscounts > 0 && cartDiscountAmount > 0 && (
                      <span className="text-[10px] text-muted-foreground">
                        Items: {currencySymbol}{totalItemDiscounts.toFixed(2)} · Cart: {currencySymbol}{cartDiscountAmount.toFixed(2)}
                      </span>
                    )}
                  </div>
                </div>
              );
            }
            return (
              <div className="flex justify-between items-center text-xs text-muted-foreground">
                <span>Discount</span>
                <span className="tabular-nums font-medium">
                  {currencySymbol}0.00
                </span>
              </div>
            );
          })()}

          {/* Tax */}
          <div className="flex justify-between items-center text-xs text-muted-foreground">
            <span>Tax</span>
            <span className="font-semibold text-foreground tabular-nums">
              {(() => {
                let totalTax = 0;
                currentProducts?.forEach((product) => {
                  totalTax += parseFloat(product.tax_amount || 0);
                });
                return `${currencySymbol}${totalTax.toFixed(2)}`;
              })()}
            </span>
          </div>

          <Separator className="my-1.5 bg-border/70" />

          {/* Grand Total */}
          <div className="flex items-center justify-between pt-0.5">
            <div className="flex items-center gap-2">
              {qtyUpdating ? (
                <Loader2 className="h-4 w-4 animate-spin text-primary" />
              ) : null}
              <div>
                <span className="text-xs font-bold uppercase tracking-wider text-foreground">
                  {currentSaleData ? "Sale Total" : "Grand Total"}
                </span>
                <span className="ml-1.5 text-[11px] font-normal text-muted-foreground">
                  ({currentProducts?.length || 0}{" "}
                  {currentProducts?.length === 1 ? "item" : "items"})
                </span>
              </div>
            </div>
            <div className="text-xl sm:text-2xl font-black tracking-tight text-primary tabular-nums">
              {currencySymbol}
              {calculateTotal().toFixed(2)}
            </div>
          </div>

          {/* Finalized Sale Payment Breakdown */}
          {currentSaleData && (
            <>
              <Separator className="my-1.5 bg-border/70" />
              <div className="flex justify-between items-center text-xs text-muted-foreground">
                <span>Amount Paid</span>
                <span className="font-semibold text-emerald-600 dark:text-emerald-400 tabular-nums">
                  {currencySymbol}
                  {parseFloat(paidAmount || currentSaleData.paid_amount || 0).toFixed(2)}
                </span>
              </div>
              <div className="flex justify-between items-center text-xs pt-0.5">
                <span className="font-bold text-foreground">Balance Due</span>
                <span
                  className={`font-bold tabular-nums text-sm ${balanceDue > 0.001
                    ? "text-destructive font-black"
                    : "text-emerald-600 dark:text-emerald-400 font-semibold"
                    }`}
                >
                  {balanceDue > 0.001
                    ? `${currencySymbol}${balanceDue.toFixed(2)}`
                    : "Fully Paid"}
                </span>
              </div>
            </>
          )}
        </div>

        {/* Action Buttons (Mobile only fallback) */}
        <div className="lg:hidden grid grid-cols-4 gap-2 pt-1">
          <Button
            variant="outline"
            className="h-10 flex-col gap-0.5 px-1 border-border text-muted-foreground hover:text-destructive hover:border-destructive/40 hover:bg-destructive/10 rounded-lg transition-all shadow-2xs"
            onClick={currentSaleData ? onExitSale : onCancelOrder}
            title={currentSaleData ? "Exit Sale View" : "Cancel"}
          >
            {currentSaleData ? <X size={15} /> : <Trash2 size={15} />}
            <span className="text-[9px] font-bold uppercase tracking-tight">
              {currentSaleData ? "Exit" : "Cancel"}
            </span>
          </Button>

          <Button
            variant="outline"
            className="h-10 flex-col gap-0.5 px-1 border-border text-muted-foreground hover:text-foreground hover:bg-muted/80 rounded-lg transition-all shadow-2xs"
            onClick={fetchRecentOrders}
            title="Recent Orders"
          >
            <List size={15} />
            <span className="text-[9px] font-bold uppercase tracking-tight">
              Recent
            </span>
          </Button>

          <Button
            variant="outline"
            disabled={Boolean(currentSaleData)}
            className="h-10 flex-col gap-0.5 px-1 border-border text-muted-foreground hover:text-foreground hover:bg-muted/80 rounded-lg transition-all shadow-2xs disabled:opacity-40"
            onClick={onHoldOrder}
            title={currentSaleData ? "Cannot hold finalized sale" : "Hold Order"}
          >
            <Pause size={15} />
            <span className="text-[9px] font-bold uppercase tracking-tight">
              Hold
            </span>
          </Button>

          {hasPermission("pos payment create") && (
            <Button
              variant="default"
              className="h-10 flex-col gap-0.5 px-1 bg-primary text-primary-foreground hover:bg-primary/90 shadow-xs border-transparent rounded-lg disabled:opacity-50"
              onClick={() => {
                if (cartDuplicateSerials.length > 0) {
                  if (showToast) {
                    showToast(
                      `Cannot proceed to checkout: Duplicate serial number(s) found in cart: ${cartDuplicateSerials.join(", ")}. Please remove duplicate items first.`,
                      "error",
                    );
                  }
                  return;
                }
                completeOrder();
              }}
              disabled={completing || cartDuplicateSerials.length > 0}
              title={
                cartDuplicateSerials.length > 0
                  ? `Cannot checkout: Duplicate serial numbers (${cartDuplicateSerials.join(", ")}) in cart`
                  : currentSaleData
                    ? balanceDue > 0.001
                      ? "Finalize Payment"
                      : "View Invoice"
                    : "Checkout"
              }
            >
              {completing ? (
                <Loader2 size={15} className="animate-spin" />
              ) : (
                <CreditCard size={15} />
              )}
              <span className="text-[9px] font-bold uppercase tracking-tight">
                {completing
                  ? "Loading..."
                  : currentSaleData
                    ? balanceDue > 0.001
                      ? "Pay Balance"
                      : "Invoice"
                    : "Checkout"}
              </span>
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};

CartSidebar.propTypes = {
  customer: PropTypes.object,
  customerSearchExpanded: PropTypes.bool,
  setCustomerSearchExpanded: PropTypes.func.isRequired,
  customerLoading: PropTypes.bool,
  customers: PropTypes.array,
  customerNameLabel: PropTypes.func.isRequired,
  debouncedSearchCustomers: PropTypes.func.isRequired,
  cart: PropTypes.object.isRequired,
  setCart: PropTypes.func.isRequired,
  fetchCustomer: PropTypes.func.isRequired,
  updateCart: PropTypes.func.isRequired,
  hasPermission: PropTypes.func.isRequired,
  setModalOpen: PropTypes.func.isRequired,
  searchInputRef: PropTypes.any,
  searchDropdownProducts: PropTypes.func,
  showToast: PropTypes.func,
  setFilterModalOpen: PropTypes.func.isRequired,
  selectedBrand: PropTypes.any,
  priceFilter: PropTypes.object,
  currentProducts: PropTypes.array,
  clearAllItems: PropTypes.func.isRequired,
  currencySymbol: PropTypes.string,
  localQuantities: PropTypes.object,
  handleQuantityChange: PropTypes.func.isRequired,
  updatingItemIds: PropTypes.array,
  handleEditCartItem: PropTypes.func.isRequired,
  removeCartItem: PropTypes.func.isRequired,
  calculateSubTotal: PropTypes.func.isRequired,
  calculateTotal: PropTypes.func.isRequired,
  qtyUpdating: PropTypes.bool,
  fetchRecentOrders: PropTypes.func.isRequired,
  onExitSale: PropTypes.func,
  onCancelOrder: PropTypes.func,
  onHoldOrder: PropTypes.func,
  completing: PropTypes.bool,
  completeOrder: PropTypes.func.isRequired,
  onClaimWarranty: PropTypes.func,
  addCartItem: PropTypes.func,
};

export default CartSidebar;
